import React, { useCallback, useEffect, useRef } from "react"
import { Linking, Pressable, View } from "react-native"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetView,
} from "@gorhom/bottom-sheet"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"

import { BodyBold, H2, H5, Small, SmallBold } from "@/components/standard/BFText"
import { type CommunityEventFragment } from "@/generated/graphql"
import { Button } from "@/components/reusables/ui/button"

import CalendarDot from "phosphor-react-native/src/icons/CalendarDot"
import Ticket from "phosphor-react-native/src/icons/Ticket"
import YoutubeLogo from "phosphor-react-native/src/icons/YoutubeLogo"
import ArrowSquareOut from "phosphor-react-native/src/icons/ArrowSquareOut"
import { BFColors } from "@/constants/Colors"
import MapPin from "phosphor-react-native/src/icons/MapPin"
import { Separator } from "@/components/reusables/ui/separator"
import { Image } from "expo-image"
import { formatEventDateTime } from "@/screens/events/utils"
import {
  eventDetailsViewed,
  registerOnEventbriteClicked,
} from "@/analytics/events/communityEvents"

interface EventDetailSheetProps {
  visible: boolean
  event: CommunityEventFragment | null
  onClose: () => void
}

const EventDetailSheet: React.FC<EventDetailSheetProps> = ({
  visible,
  event,
  onClose,
}) => {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        onPress={() => {
          onClose()
        }}
      />
    ),
    [onClose]
  )

  useEffect(() => {
    if (visible) {
      eventDetailsViewed({
        eventName: event?.name || "unknown",
        eventLocation: event?.market?.slug || "virtual",
      })
    }
  }, [visible])

  useEffect(() => {
    if (!visible) {
      bottomSheetModalRef.current?.dismiss()
    } else {
      bottomSheetModalRef.current?.present()
    }
  }, [visible])

  const handleRegister = () => {
    registerOnEventbriteClicked({
      eventName: event?.name || "unknown",
      eventLocation: event?.market?.slug || "virtual",
    })
    if (event?.eventUrl) {
      void Linking.openURL(event.eventUrl)
    }
  }

  const handleMoreDetails = () => {
    if (event?.eventUrl) {
      void Linking.openURL(event.eventUrl)
    }
  }

  if (!event) return null

  const isVirtual = event.eventType === "VIRTUAL"

  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      backdropComponent={renderBackdrop}
      backgroundStyle={{ backgroundColor: "#FFFFFF" }}
      handleIndicatorStyle={{ backgroundColor: "#F0F0F0" }}
      snapPoints={["90%"]}
      onChange={(index) => {
        if (index === -1) {
          onClose()
        }
      }}
    >
      <BottomSheetView>
        {/* Event Title */}
        <View className="px-6 pt-10 pb-6">
          <H2 className="text-center text-midnight-blue">{event.name}</H2>
        </View>

        {/* Event Details */}
        <View className="flex-1 px-6 gap-6">
          {/* Badge and Location */}
          <View className="flex-row justify-center items-center gap-2">
            {isVirtual ? (
              <View className="bg-calm-blue rounded-sm px-2 py-1">
                <SmallBold className="text-white uppercase">Virtual</SmallBold>
              </View>
            ) : (
              <View className="bg-teal rounded-sm px-2 py-1">
                <SmallBold className="text-midnight-blue uppercase">
                  In Person
                </SmallBold>
              </View>
            )}

            {!isVirtual && (
              <BodyBold className="text-midnight-blue">
                {event.market?.name || "In Person"}
              </BodyBold>
            )}
          </View>

          {/* Date, Time, Location */}
          <View className="items-center gap-1">
            <Horizontal className="items-center gap-2">
              <Ticket size={16} color={BFColors.midnightBlue} />
              <Small className="text-midnight-blue">Free Event</Small>
              <Separator orientation={"vertical"} />
              <CalendarDot size={16} color={BFColors.midnightBlue} />
              <Small className="text-midnight-blue">
                {formatEventDateTime(event.startTime)}
              </Small>
            </Horizontal>

            {isVirtual ? (
              <Horizontal className="items-center gap-2">
                <YoutubeLogo size={16} color={BFColors.midnightBlue} />
                <Small className="text-midnight-blue">Online</Small>
              </Horizontal>
            ) : (
              <Pressable
                testID={"event-address"}
                onPress={() => {
                  void Linking.openURL(
                    `maps:q=${event.address},${event.market?.name || ""}`
                  )
                }}
              >
                <Horizontal className="items-center gap-2">
                  <MapPin size={16} color={BFColors.midnightBlue} />
                  <Small className="text-midnight-blue">{event.address}</Small>
                </Horizontal>
              </Pressable>
            )}
          </View>

          {/* Overview */}
          <Vertical className="flex-1 gap-2">
            <H5 className="text-midnight-blue">Overview</H5>
            <Small className="flex-1 text-midnight-blue" numberOfLines={5}>
              {event.description}
            </Small>

            <Pressable
              testID={"more-details"}
              className="flex-row items-center mt-1"
              onPress={handleMoreDetails}
            >
              <Small className="text-calm-blue font-bold mr-1">
                More details
              </Small>
            </Pressable>
          </Vertical>

          {/* Questions Section */}
          <Vertical className="gap-2 mt-4">
            <H5 className="text-midnight-blue">Questions?</H5>
            <Small className="text-midnight-blue">
              Feel free to reach out. We&apos;re here to help.
            </Small>

            {/* Contact Card */}
            <Pressable
              testID={"contact-team"}
              className="border border-light-grey rounded-md p-3 mt-2 flex-row items-center"
              onPress={() => {
                void Linking.openURL("mailto:team@backflip.com")
              }}
            >
              <View className="size-10 rounded-full bg-gray-200 mr-3">
                <Image
                  style={{ width: 40, height: 40, borderRadius: 20 }}
                  source={require("@/assets/images/Mariah.jpg")}
                />
              </View>
              <View>
                <BodyBold className="text-midnight-blue">
                  Mariah Schmidt
                </BodyBold>
                <Small className="text-midnight-blue">
                  Sr. Director of Community
                </Small>
                <Small className="text-calm-blue">team@backflip.com</Small>
              </View>
            </Pressable>
          </Vertical>
        </View>

      {/* Register Button */}
      <View className="bg-white px-6 pt-4 pb-6">
        <Button
          testID={"register"}
          className="bg-calm-blue rounded-full py-3"
          onPress={handleRegister}
        >
          <BodyBold>Register on Eventbrite</BodyBold>
          <ArrowSquareOut size={20} weight={"bold"} color={BFColors.offWhite} />
        </Button>
      </View>
      </BottomSheetView>
    </BottomSheetModal>
  )
}

export default EventDetailSheet
